import { useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@store";
import {
  calculateAutonomySegments,
  AutonomyData,
  AutonomySegment,
  calculateSegmentDistance,
} from "../utils/autonomyUtils";
import { resetAutonomy } from "../../../store/autonomy/autonomySlice";

interface UseVehicleAutonomyProps {
  segments?: any[];
}

interface UseVehicleAutonomyReturn {
  vehicleRangeSegments: AutonomySegment[];
  autonomyData: AutonomyData;
  autonomyLimitPosition: { lat: number; lng: number } | null;
  hasAutonomyForFullRoute: boolean;
}

export const useVehicleAutonomy = ({ segments }: UseVehicleAutonomyProps): UseVehicleAutonomyReturn => {
  const dispatch = useDispatch();
  const autonomyState = useAppSelector((state) => state.autonomy);
  const { consumeAutonomy, enableAutonomy, fuelLevelAutonomy, capacityAutonomy, via } = autonomyState;

  const { segments: vehicleRangeSegments, data: autonomyData } = useMemo(() => {
    const currentFuelLevel = Number(fuelLevelAutonomy) || 0;
    const fuelConsumption = Number(consumeAutonomy) || 0;
    const tankCapacity = Number(capacityAutonomy) || 0;

    if (!segments || segments.length === 0 || !enableAutonomy) {
      return {
        segments: [],
        data: {
          isEnabled: enableAutonomy,
          maxRangeKm: 0,
          actualRangeKm: 0,
          fuelLevel: currentFuelLevel,
          consumption: fuelConsumption,
        },
      };
    }

    const refuelingStops = via.filter((stop) => stop.category === "REFUELING");

    return calculateAutonomySegments(
      segments,
      enableAutonomy,
      currentFuelLevel,
      fuelConsumption,
      tankCapacity,
      refuelingStops
    );
  }, [segments, enableAutonomy, fuelLevelAutonomy, consumeAutonomy, capacityAutonomy, via]);

  type LatLng = { lat: number; lng: number };

  const hasAutonomyForFullRoute = useMemo(() => {
    if (!segments?.length || !enableAutonomy) return false;

    const currentFuelLevel = Number(fuelLevelAutonomy) || 0;
    const fuelConsumption = Number(consumeAutonomy) || 0;
    const tankCapacity = Number(capacityAutonomy) || 0;

    if (currentFuelLevel <= 0 || fuelConsumption <= 0) return false;

    const initialRangeKm = currentFuelLevel * fuelConsumption;
    const fullTankRangeKm = tankCapacity * fuelConsumption;

    const segmentPoints: LatLng[] = segments[0]?.points || [];
    if (segmentPoints.length < 2) return false;

    const totalRouteDistance = calculateSegmentDistance(segmentPoints);
    const refuelingStops = via.filter((stop) => stop.category === "REFUELING");

    const calculateMinDistanceToRoute = (stopLat: number, stopLng: number): number =>
      segmentPoints.reduce((min: number, routePoint: LatLng) => {
        const distance = Math.sqrt(Math.pow(stopLat - routePoint.lat, 2) + Math.pow(stopLng - routePoint.lng, 2)) * 111;
        return Math.min(min, distance);
      }, Infinity);

    const countValidRefuelingStops = (): number =>
      refuelingStops.reduce((count: number, stop) => {
        const { lat, lng } = stop.position ?? {};
        if (lat == null || lng == null) return count;
        return calculateMinDistanceToRoute(lat, lng) <= 1 ? count + 1 : count;
      }, 0);

    if (refuelingStops.length > 0 && tankCapacity > 0) {
      const validStops = countValidRefuelingStops();
      const totalAutonomyWithStops = initialRangeKm + validStops * fullTankRangeKm;
      return totalAutonomyWithStops >= totalRouteDistance;
    }

    return initialRangeKm >= totalRouteDistance;
  }, [segments, enableAutonomy, fuelLevelAutonomy, consumeAutonomy, capacityAutonomy, via]);

  const autonomyLimitPosition = useMemo(() => {
    if (!vehicleRangeSegments.length || !enableAutonomy) {
      return null;
    }

    const lastSegment = vehicleRangeSegments[vehicleRangeSegments.length - 1];

    if (!lastSegment?.points?.length) {
      return null;
    }

    const lastPoint = lastSegment.points[lastSegment.points.length - 1];

    if (!lastPoint || isNaN(lastPoint.lat) || isNaN(lastPoint.lng)) {
      return null;
    }

    const position = {
      lat: lastPoint.lat,
      lng: lastPoint.lng,
    };

    return position;
  }, [vehicleRangeSegments, enableAutonomy]);

  useEffect(() => {
    return () => {
      dispatch(resetAutonomy());
    };
  }, [dispatch]);

  return {
    vehicleRangeSegments,
    autonomyData,
    autonomyLimitPosition,
    hasAutonomyForFullRoute,
  };
};
